import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import clsx from 'clsx';

import {
  COLORS,
  ids,
  modalMessages,
  APP_ROUTES,
  ModalTypes,
} from 'apps/web_forms/utils/constants';
import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import Modal from 'apps/web_forms/components/commons/Modal';
import Select from 'apps/web_forms/components/commons/Select';
import { getDraftAuthors } from 'apps/web_forms/api/api';
import { useHistory } from 'react-router-dom';
import { reassignDraft as reassignDraftCall } from 'apps/web_forms/api/api';
import { resetModal, fetchDraftList, setModal } from 'apps/web_forms/slices/appSlice';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';

const StyledDiv = styled.div`
  font-weight: 400;
  font-family: 'Helvetica', 'Roboto';
  width: 100%;

  > div {
    width: 100%;
  }

  .select-loader {
    justify-content: center;
  }

  .invalid {
    border: 2px solid ${COLORS.red};
    outline: none;
  }
`;

export const ReassignDraftModal = ({
  saveButtonText,
  extraProps,
  resetSelectedModal,
}) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loadingOptions, setLoading] = useState(true);
  const [rawResponse, setRawResponse] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState();
  const dispatch = useDispatch();
  const history = useHistory();
  const { draftIdToReassign: draftId } = extraProps;

  useEffect(() => {
    async function loadDraftAssignees() {
      setLoading(true);
      try {
        if (draftId) {
          const response = await getDraftAuthors(draftId);
          setRawResponse(response);
        }
      } catch (e) {
        setHasError(true);
        setErrorMessage('Unable to load assignees, please contact support for assistance');
      } finally {
        setLoading(false);
      }
    }

    loadDraftAssignees();
  }, [draftId]);

  const selectOptions = useMemo(() => {
    return rawResponse.map((possibleAssignee) => {
      return { value: possibleAssignee.value, label: possibleAssignee.label };
    });
  }, [rawResponse]);

  const reassignDraft = async (assignee) => {
    if (assignee) {
      setLoading(true);
      try {
        const response = await reassignDraftCall(
          draftId,
          assignee.id,
          assignee.type
        );

        if (response.status == 200) {
          setLoading(false);
          dispatch(fetchDraftList());
          dispatch(resetModal());
          openReassignDraftSuccessModal();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        setLoading(false);
        console.error(e);
        history.push(APP_ROUTES.getErrorPageByCode(500));
      }
    } else {
      setHasError(true);
      setErrorMessage('A person or device must be selected to reassign the draft');
      console.error('assignee not selected');
    }
  };

  const openReassignDraftSuccessModal = async () => {
    dispatch(
      setModal({
        type: ModalTypes.DRAFT_REASSIGNMENT_SUCCESS_MODAL,
        resetSelectedModal: resetSelectedModal,
      })
    );
  };

  const handleOnChange = (e) => {
    setHasError(false);
    setErrorMessage('');

    let selected = rawResponse.find((assignee) => assignee.value === e);
    // Use the selectedAssignee for further processing
    setSelectedAssignee(selected);
  };

  return (
    <Modal
      mainMessage={modalMessages.reassignDraft.main}
      helpTip={modalMessages.reassignDraft.tip}
      isOpen={true}
      setIsOpen={resetSelectedModal}
      buttons={[
        {
          id: ids.ReassignDraftModalIds.CancelButton,
          label: 'Cancel',
          onClick: resetSelectedModal,
          filled: false,
          disabled: false,
        },
        {
          id: ids.ReassignDraftModalIds.SaveButton,
          label: saveButtonText,
          onClick: () => reassignDraft(selectedAssignee),
          filled: true,
          disabled: hasError,
        },
      ]}
    >
      <StyledDiv className='react-form-control'>
        <>
          {loadingOptions ? (
            <Loader type="Oval" color={COLORS.primary} width={30} />
          ) : (
            <>
              <Select
                options={selectOptions}
                loadingResource={loadingOptions}
                onSelect={handleOnChange}
                id='authorable'
                name='authorable'
              />
              {hasError && errorMessage && (
                <ErrorMessage
                  id={`${ids.ReassignDraftModalIds.ReassignDraftModal}_errmsg`}
                >
                  {errorMessage}
                </ErrorMessage>
              )}
            </>
          )}
        </>
      </StyledDiv>
    </Modal>
  );
};

ReassignDraftModal.propTypes = {
  mainMessage: PropTypes.string,
  helpTip: PropTypes.string,
  saveButtonText: PropTypes.string
};

ReassignDraftModal.defaultProps = {
  mainMessage: modalMessages.saveDraft.main,
  helpTip: modalMessages.saveDraft.tip,
  saveButtonText: 'Save',
};

export default ReassignDraftModal;
